import { useState } from 'react'
import { Card, Form, Button, Row, Col, Alert, Table } from 'react-bootstrap'
import { toast } from 'react-toastify'
import { extensionsApi } from '../services/api'

interface CreatedExtension {
  extension: string
  name: string
  password: string
}

export default function Wizard() {
  const [step, setStep] = useState(1)
  const [loading, setLoading] = useState(false)
  const [results, setResults] = useState<{ created: number; skipped: number; extensions: CreatedExtension[] } | null>(null)

  // Form data
  const [startNumber, setStartNumber] = useState(100)
  const [endNumber, setEndNumber] = useState(110)
  const [nameTemplate, setNameTemplate] = useState('Nebenstelle {ext}')
  const [passwordMode, setPasswordMode] = useState('auto')
  const [staticPassword, setStaticPassword] = useState('')
  const [voicemailEnabled, setVoicemailEnabled] = useState(true)
  const [callRecording, setCallRecording] = useState(false)
  const [hintEnabled, setHintEnabled] = useState(true)

  const handleBulkCreate = async () => {
    if (endNumber < startNumber) {
      toast.error('Endnummer muss größer oder gleich Startnummer sein')
      return
    }

    if (passwordMode === 'static' && !staticPassword) {
      toast.error('Bitte Passwort eingeben')
      return
    }

    setLoading(true)
    try {
      const response = await extensionsApi.bulkCreate({
        start_number: startNumber,
        end_number: endNumber,
        name_template: nameTemplate,
        password_mode: passwordMode,
        static_password: passwordMode === 'static' ? staticPassword : null,
        voicemail_enabled: voicemailEnabled,
        call_recording: callRecording,
        hint_enabled: hintEnabled,
      })

      // Backend returns array of extensions
      const extensions = response.data || []
      const created = extensions.length
      const skipped = (endNumber - startNumber + 1) - created
      setResults({ created, skipped, extensions })
      setStep(3)
      toast.success(`${created} Nebenstellen erstellt`)
    } catch (error) {
      toast.error('Fehler beim Erstellen der Nebenstellen')
    } finally {
      setLoading(false)
    }
  }

  return (
    <div>
      <h4 className="mb-4">
        <i className="bi bi-magic me-2"></i>
        Assistent
      </h4>

      <Row>
        <Col md={8}>
          {step === 1 && (
            <Card>
              <Card.Header>
                <i className="bi bi-telephone-plus me-2"></i>
                Mehrere Nebenstellen erstellen
              </Card.Header>
              <Card.Body>
                <p className="text-muted mb-4">
                  Erstellen Sie schnell mehrere Nebenstellen auf einmal. Ideal für die Ersteinrichtung.
                </p>

                <Row className="mb-3">
                  <Col md={6}>
                    <Form.Group>
                      <Form.Label>Startnummer *</Form.Label>
                      <Form.Control
                        type="number"
                        value={startNumber}
                        onChange={(e) => setStartNumber(parseInt(e.target.value))}
                        min={1}
                      />
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group>
                      <Form.Label>Endnummer *</Form.Label>
                      <Form.Control
                        type="number"
                        value={endNumber}
                        onChange={(e) => setEndNumber(parseInt(e.target.value))}
                        min={startNumber}
                      />
                    </Form.Group>
                  </Col>
                </Row>

                <Form.Group className="mb-3">
                  <Form.Label>Namensvorlage</Form.Label>
                  <Form.Control
                    type="text"
                    value={nameTemplate}
                    onChange={(e) => setNameTemplate(e.target.value)}
                  />
                  <Form.Text className="text-muted">
                    Verwenden Sie {'{ext}'} als Platzhalter für die Nebenstellennummer
                  </Form.Text>
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Label>Passwort</Form.Label>
                  <Form.Check
                    type="radio"
                    name="passwordMode"
                    label="Automatisch generieren (empfohlen)"
                    checked={passwordMode === 'auto'}
                    onChange={() => setPasswordMode('auto')}
                  />
                  <Form.Check
                    type="radio"
                    name="passwordMode"
                    label="Gleiches Passwort für alle"
                    checked={passwordMode === 'static'}
                    onChange={() => setPasswordMode('static')}
                  />
                  {passwordMode === 'static' && (
                    <Form.Control
                      type="text"
                      value={staticPassword}
                      onChange={(e) => setStaticPassword(e.target.value)}
                      placeholder="Passwort eingeben"
                      className="mt-2"
                    />
                  )}
                </Form.Group>

                <hr />

                <Row>
                  <Col md={4}>
                    <Form.Check
                      type="switch"
                      label="Voicemail aktivieren"
                      checked={voicemailEnabled}
                      onChange={(e) => setVoicemailEnabled(e.target.checked)}
                    />
                  </Col>
                  <Col md={4}>
                    <Form.Check
                      type="switch"
                      label="Anrufaufzeichnung"
                      checked={callRecording}
                      onChange={(e) => setCallRecording(e.target.checked)}
                    />
                  </Col>
                  <Col md={4}>
                    <Form.Check
                      type="switch"
                      label="BLF/Hints"
                      checked={hintEnabled}
                      onChange={(e) => setHintEnabled(e.target.checked)}
                    />
                  </Col>
                </Row>
              </Card.Body>
              <Card.Footer className="text-end">
                <Button variant="primary" onClick={() => setStep(2)}>
                  Weiter <i className="bi bi-arrow-right ms-1"></i>
                </Button>
              </Card.Footer>
            </Card>
          )}

          {step === 2 && (
            <Card>
              <Card.Header>Zusammenfassung</Card.Header>
              <Card.Body>
                <Alert variant="info">
                  <strong>{endNumber - startNumber + 1} Nebenstellen</strong> werden erstellt
                  (von {startNumber} bis {endNumber})
                </Alert>

                <Table className="mb-0">
                  <tbody>
                    <tr>
                      <td>Nummernbereich:</td>
                      <td><strong>{startNumber} - {endNumber}</strong></td>
                    </tr>
                    <tr>
                      <td>Namensvorlage:</td>
                      <td><strong>{nameTemplate}</strong></td>
                    </tr>
                    <tr>
                      <td>Passwort-Modus:</td>
                      <td><strong>{passwordMode === 'auto' ? 'Automatisch' : 'Statisch'}</strong></td>
                    </tr>
                    <tr>
                      <td>Voicemail:</td>
                      <td>{voicemailEnabled ? <i className="bi bi-check-circle text-success"></i> : <i className="bi bi-x-circle text-muted"></i>}</td>
                    </tr>
                    <tr>
                      <td>Aufzeichnung:</td>
                      <td>{callRecording ? <i className="bi bi-check-circle text-success"></i> : <i className="bi bi-x-circle text-muted"></i>}</td>
                    </tr>
                    <tr>
                      <td>BLF/Hints:</td>
                      <td>{hintEnabled ? <i className="bi bi-check-circle text-success"></i> : <i className="bi bi-x-circle text-muted"></i>}</td>
                    </tr>
                  </tbody>
                </Table>
              </Card.Body>
              <Card.Footer className="d-flex justify-content-between">
                <Button variant="secondary" onClick={() => setStep(1)}>
                  <i className="bi bi-arrow-left me-1"></i> Zurück
                </Button>
                <Button variant="primary" onClick={handleBulkCreate} disabled={loading}>
                  {loading ? 'Erstelle...' : 'Nebenstellen erstellen'}
                </Button>
              </Card.Footer>
            </Card>
          )}

          {step === 3 && results && (
            <Card>
              <Card.Header className="bg-success text-white">
                <i className="bi bi-check-circle me-2"></i>
                Erledigt!
              </Card.Header>
              <Card.Body>
                <Alert variant="success">
                  <strong>{results.created}</strong> Nebenstellen wurden erstellt
                  {results.skipped > 0 && <>, <strong>{results.skipped}</strong> übersprungen (bereits vorhanden)</>}
                </Alert>

                <h6>Erstellte Nebenstellen:</h6>
                <div style={{ maxHeight: '300px', overflow: 'auto' }}>
                  <Table size="sm">
                    <thead>
                      <tr>
                        <th>Nebenstelle</th>
                        <th>Name</th>
                        <th>SIP-Passwort</th>
                      </tr>
                    </thead>
                    <tbody>
                      {results.extensions.map((ext, idx) => (
                        <tr key={idx}>
                          <td><strong>{ext.extension}</strong></td>
                          <td>{ext.name}</td>
                          <td><code>{ext.password}</code></td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </div>
              </Card.Body>
              <Card.Footer className="text-end">
                <Button variant="primary" onClick={() => { setStep(1); setResults(null); }}>
                  Weitere erstellen
                </Button>
              </Card.Footer>
            </Card>
          )}
        </Col>

        <Col md={4}>
          <Card>
            <Card.Header>Hilfe</Card.Header>
            <Card.Body>
              <h6>Bulk-Erstellung</h6>
              <p className="text-muted small">
                Der Assistent erstellt automatisch Nebenstellen im angegebenen Nummernbereich.
                Bestehende Nebenstellen werden übersprungen.
              </p>

              <h6>Namensvorlage</h6>
              <p className="text-muted small">
                <code>{'{ext}'}</code> wird durch die Nebenstellennummer ersetzt.<br />
                Beispiel: "Büro {'{ext}'}" → "Büro 100", "Büro 101", etc.
              </p>

              <h6>Passwörter</h6>
              <p className="text-muted small">
                Automatisch generierte Passwörter sind sicherer.
                Sie werden nach der Erstellung angezeigt.
              </p>
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </div>
  )
}
